import { Navigation } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import 'swiper/css';
import 'swiper/css/navigation';
import {
  RestaurantTag,
  RestaurantTagsResponse,
} from '@/lib/types/customer/RestaurantTags';
import Image from 'next/image';
import { useEffect } from 'react';
import { ChevronLeftIcon } from '@heroicons/react/24/solid';
import { ChevronRightIcon } from '@heroicons/react/24/outline';
import { useRestaurantTagsDataStore } from '@/stores/cutomer/restaurantTags';
import { useTranslations } from 'next-intl';
import { STOREFRONT } from '@/lib/utils/translation';
import useRestaurantTags from '@/hooks/useRestaurantTags';

import defaultImage from '../../public/search/dflt_kat.png';
import clsx from 'clsx';

type Props = {
  tagsData?: RestaurantTag[];
  handleTag: (tag: RestaurantTag, idx?: number) => void;
  isChildTagCarousel?: boolean;
  tagsResData?: RestaurantTagsResponse;
  carouselId: string;
};

const TagsCarousel = ({
  tagsData,
  handleTag,
  isChildTagCarousel,
  tagsResData,
  carouselId,
}: Props) => {
  const t = useTranslations();

  const scrollToSlideFirstTime = useRestaurantTagsDataStore(
    (state) => state.scrollToSlideFirstTime
  );
  const setScrollToSlideFirstTime = useRestaurantTagsDataStore(
    (state) => state.setScrollToSlideFirstTime
  );
  const setShowViewAllTag = useRestaurantTagsDataStore(
    (state) => state.setShowViewAllTag
  );
  const setIsChildTag = useRestaurantTagsDataStore(
    (state) => state.setIsChildTag
  );
  const selectedSearchableTags = useRestaurantTagsDataStore(
    (state) => state.selectedSearchableTags
  );
  const childrenTags = useRestaurantTagsDataStore(
    (state) => state.childrenTags
  );
  const selectedParentTag = useRestaurantTagsDataStore(
    (state) => state.selectedParentTag
  );
  const selectedChildTags = useRestaurantTagsDataStore(
    (state) => state.selectedChildTags
  );
  const swiperRefs = useRestaurantTagsDataStore((state) => state.swiperRefs);
  const swiperRefsInstance = useRestaurantTagsDataStore((state) =>
    state.swiperRefs.get(carouselId)
  );

  const showSwiperNav = useRestaurantTagsDataStore(
    (state) => state.showSwiperNav
  );
  const setShowSwiperNav = useRestaurantTagsDataStore(
    (state) => state.setShowSwiperNav
  );

  const setSwiperRefs = useRestaurantTagsDataStore(
    (state) => state.setSwiperRefs
  );

  const tagsDataIDs = tagsData?.map((tag) => tag.id);
  const { getParamsTagIds, getSearchParams, scrollToSlide }: any =
    useRestaurantTags();

  useEffect(() => {
    if (scrollToSlideFirstTime && swiperRefsInstance && selectedParentTag) {
      const parentTagIdx =
        tagsResData?.data?.findIndex(
          (item) => item.id === selectedParentTag?.id
        ) ?? -1;
      if (parentTagIdx > -1) {
        scrollToSlide(carouselId, parentTagIdx);
      }

      if (childrenTags?.length > 0 && selectedChildTags?.length > 0) {
        const childTagIdx = childrenTags?.findIndex(
          (item) => item.id === selectedChildTags?.[0]?.id
        );
        if (childTagIdx > -1) {
          scrollToSlide(carouselId, childTagIdx);
        }
      }

      setScrollToSlideFirstTime(false);
    }
  }, [
    scrollToSlideFirstTime,
    swiperRefsInstance,
    selectedChildTags,
    selectedParentTag,
    carouselId,
    childrenTags,
    scrollToSlide,
    setScrollToSlideFirstTime,
    tagsResData,
  ]);

  useEffect(() => {
    const handleSlideChange = (id: string) => {
      setShowSwiperNav(id);
    };

    swiperRefs.forEach((_, id) => {
      const swiper = swiperRefs.get(id);
      if (swiper) {
        swiper.onAny(() => {
          handleSlideChange(id);
        });
      }
    });

    return () => {
      swiperRefs.forEach((swiper) => swiper?.off('slideChange'));
    };
  }, [swiperRefs, setShowSwiperNav]);

  // Handle next and previous navigation
  const handleNext = () => {
    swiperRefsInstance?.slideNext();
  };

  const handlePrev = () => {
    swiperRefsInstance?.slidePrev();
  };

  return (
    <div className="mb-8 w-full">
      <div className="relative flex flex-col items-center justify-center lg:flex-row">
        <div className="w-full lg:w-[92%]">
          <Swiper
            id={carouselId}
            onSwiper={(swiper) => setSwiperRefs(swiper, carouselId)}
            modules={[Navigation]}
            spaceBetween={0}
            slidesPerGroup={2}
            slidesPerView={'auto'}
            className="scroll-spy-menu-slider !m-0"
          >
            {tagsData?.map((tag, idx) => (
              <SwiperSlide
                key={tag?.id}
                className="!flex !w-auto min-w-min flex-col"
              >
                <div
                  className="mx-4 space-y-2 text-center"
                  role="button"
                  onClick={() => {
                    if (tag.link) {
                      window.open(tag.link, '_self');
                    } else {
                      handleTag(tag, idx);
                    }
                  }}
                >
                  <div
                    className={clsx(
                      isChildTagCarousel
                        ? 'h-[65px] w-[65px]  md:h-[94px] md:w-[94px]'
                        : 'h-[80px] w-[80px] md:h-[114px] md:w-[114px]',
                      'relative overflow-hidden rounded-[28.7px]'
                    )}
                  >
                    {tag.media?.image?.[0]?.thumb_url ? (
                      <Image
                        src={tag.media.image[0].thumb_url}
                        alt=""
                        fill
                        className="object-cover"
                      />
                    ) : (
                      <Image
                        src={defaultImage}
                        alt="tag image"
                        fill
                        className="object-cover"
                      />
                    )}
                  </div>
                  <div
                    className={clsx(
                      isChildTagCarousel
                        ? 'text-md font-normal'
                        : 'text-lg font-bold',
                      getParamsTagIds(getSearchParams()?.tag_ids)?.includes(
                        tag.id
                      ) ||
                        (!isChildTagCarousel &&
                          childrenTags?.length > 0 &&
                          childrenTags?.[0]?.parent_id === tag.id)
                        ? 'text-primary'
                        : 'text-paragraph'
                    )}
                  >
                    {tag?.name}
                  </div>
                </div>
              </SwiperSlide>
            ))}

            {selectedSearchableTags?.length > 0 &&
              isChildTagCarousel &&
              selectedSearchableTags
                ?.filter(
                  (tag) => !tagsDataIDs?.includes(tag.id) && tag?.parent_id
                )
                ?.map((tag, idx) => (
                  <SwiperSlide
                    key={tag?.id}
                    className="!flex !w-auto min-w-min flex-col"
                  >
                    <div
                      className="mx-4 space-y-2 text-center"
                      role="button"
                      onClick={() => {
                        if (tag.link) {
                          window.open(tag.link, '_self');
                        } else {
                          handleTag(tag, idx);
                        }
                      }}
                    >
                      <div
                        className={clsx(
                          isChildTagCarousel
                            ? 'h-[65px] w-[65px]  md:h-[94px] md:w-[94px]'
                            : 'h-[80px] w-[80px] md:h-[114px] md:w-[114px]',
                          'relative overflow-hidden rounded-[28.7px]'
                        )}
                      >
                        {tag.media?.image?.[0]?.thumb_url ? (
                          <Image
                            src={tag.media.image[0].thumb_url}
                            alt=""
                            fill
                            className="object-cover"
                          />
                        ) : (
                          <Image
                            src={defaultImage}
                            alt="tag image"
                            fill
                            className="object-cover"
                          />
                        )}
                      </div>
                      <div
                        className={clsx(
                          isChildTagCarousel
                            ? 'text-md font-normal'
                            : 'text-lg font-bold',
                          getParamsTagIds(getSearchParams()?.tag_ids)?.includes(
                            tag.id
                          )
                            ? 'text-primary'
                            : 'text-paragraph'
                        )}
                      >
                        {tag?.name}
                      </div>
                    </div>
                  </SwiperSlide>
                ))}

            {isChildTagCarousel && tagsData && tagsData?.length > 14 && (
              <SwiperSlide className="!flex !w-auto min-w-min flex-col">
                <div
                  className="mx-4 space-y-2 text-center"
                  role="button"
                  onClick={() => {
                    if (isChildTagCarousel) {
                      setIsChildTag(true);
                    } else {
                      setIsChildTag(false);
                    }
                    setShowViewAllTag(true);
                  }}
                >
                  <div className="relative flex h-[115px] w-[80px] items-center justify-center overflow-hidden rounded-md md:h-[149px] md:w-[114px]">
                    <div
                      className={clsx(
                        isChildTagCarousel
                          ? 'text-md font-normal'
                          : 'text-lg font-bold',
                        'text-paragraph'
                      )}
                    >
                      {t(`${STOREFRONT.SEARCH}.view_all`)}
                    </div>
                  </div>
                </div>
              </SwiperSlide>
            )}
          </Swiper>
        </div>
        {/* Custom Navigation Buttons */}
        <div className="custom-navigation mt-3 space-x-3 text-black lg:mt-0 lg:space-x-0">
          {!showSwiperNav[carouselId].isBeginning && (
            <button
              onClick={() => handlePrev()}
              type="button"
              className={`custom-button-prev lg:absolute lg:left-0`}
              aria-label={t('aria.previous_tag_carousel')}
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-center text-paragraph hover:bg-primary hover:text-white md:h-8 md:w-8">
                <ChevronLeftIcon className="" width={16} height={16} />
              </div>
            </button>
          )}

          {!showSwiperNav[carouselId].isEnd && (
            <button
              onClick={() => handleNext()}
              type="button"
              className={`custom-button-next lg:absolute lg:right-0`}
              aria-label={t('aria.next_tag_carousel')}
            >
              <div className="flex h-6 w-6 items-center justify-center rounded-full border border-gray-300 text-center text-paragraph hover:bg-primary hover:text-white md:h-8 md:w-8">
                <ChevronRightIcon className="" width={16} height={16} />
              </div>
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TagsCarousel;
